import { API_BASE_URL } from './client';

const authHeaders = (keycloak) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${keycloak.token}`,
});

export const listPrompts = async (keycloak, params = {}) => {
  const qs = new URLSearchParams();
  if (params.kind) qs.append('kind', params.kind);
  if (params.search) qs.append('search', params.search);
  const response = await fetch(`${API_BASE_URL}/prompts/?${qs}`, {
    headers: authHeaders(keycloak),
  });
  if (!response.ok) throw new Error('Failed to fetch prompts');
  return response.json();
};

export const getPrompt = async (keycloak, promptId) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/`, {
    headers: authHeaders(keycloak),
  });
  if (!response.ok) throw new Error('Failed to fetch prompt');
  return response.json();
};

export const createPrompt = async (keycloak, data) => {
  const response = await fetch(`${API_BASE_URL}/prompts/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to create prompt');
  }
  return response.json();
};

export const updatePrompt = async (keycloak, promptId, data) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/`, {
    method: 'PATCH',
    headers: authHeaders(keycloak),
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to update prompt');
  return response.json();
};

export const resolvePromptBinding = async (keycloak, data) => {
  const response = await fetch(`${API_BASE_URL}/prompts/resolve-binding/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to resolve prompt binding');
  return response.json();
};

export const getPromptBinding = async (keycloak, targetType, targetId, slot) => {
  const response = await fetch(
    `${API_BASE_URL}/prompts/bindings/${targetType}/${targetId}/${slot}/`,
    { headers: authHeaders(keycloak) },
  );
  if (!response.ok) throw new Error('Failed to fetch binding');
  return response.json();
};

export const listPromptVersions = async (keycloak, promptId, scope) => {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : '';
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/versions/${qs}`, {
    headers: authHeaders(keycloak),
  });
  if (!response.ok) throw new Error('Failed to fetch versions');
  return response.json();
};

export const createPromptVersion = async (keycloak, promptId, data) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/versions/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to save version');
  return response.json();
};

export const diffPromptVersions = async (keycloak, promptId, versionA, versionB) => {
  const response = await fetch(
    `${API_BASE_URL}/prompts/${promptId}/versions/${versionA}/diff/${versionB}/`,
    { headers: authHeaders(keycloak) },
  );
  if (!response.ok) throw new Error('Failed to compute diff');
  return response.json();
};

export const publishPrompt = async (keycloak, promptId, data = {}) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/publish/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to publish prompt');
  return response.json();
};

export const rollbackPrompt = async (keycloak, promptId, versionId) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/rollback/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    body: JSON.stringify({ version_id: versionId }),
  });
  if (!response.ok) throw new Error('Failed to rollback');
  return response.json();
};

export const getPromptBindings = async (keycloak, promptId) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/bindings/`, {
    headers: authHeaders(keycloak),
  });
  if (!response.ok) throw new Error('Failed to fetch bindings');
  return response.json();
};

export const countPromptTokens = async (keycloak, text, modelIds) => {
  const response = await fetch(`${API_BASE_URL}/prompts/token-count/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    body: JSON.stringify({ text, model_ids: modelIds }),
  });
  if (!response.ok) throw new Error('Failed to count tokens');
  return response.json();
};

export const assistPrompt = async (keycloak, data, signal) => {
  const response = await fetch(`${API_BASE_URL}/prompts/assist/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    body: JSON.stringify(data),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Assist failed');
  }
  return response.json();
};

export const listTestCases = async (keycloak, promptId) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/test-cases/`, {
    headers: authHeaders(keycloak),
  });
  if (!response.ok) throw new Error('Failed to fetch test cases');
  return response.json();
};

export const createTestCase = async (keycloak, promptId, data) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/test-cases/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to create test case');
  return response.json();
};

export const runTestCase = async (keycloak, promptId, caseId, data = {}) => {
  const response = await fetch(
    `${API_BASE_URL}/prompts/${promptId}/test-cases/${caseId}/run/`,
    {
      method: 'POST',
      headers: authHeaders(keycloak),
      body: JSON.stringify(data),
    },
  );
  if (!response.ok) throw new Error('Failed to run test case');
  return response.json();
};

export const listPromptComments = async (keycloak, promptId) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/comments/`, {
    headers: authHeaders(keycloak),
  });
  if (!response.ok) throw new Error('Failed to fetch comments');
  return response.json();
};

export const createPromptComment = async (keycloak, promptId, data) => {
  const response = await fetch(`${API_BASE_URL}/prompts/${promptId}/comments/`, {
    method: 'POST',
    headers: authHeaders(keycloak),
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to create comment');
  return response.json();
};
